import React, { useState } from "react";
import { Row, Col, Modal } from "antd";
import { Pie } from "@ant-design/plots";
import styled from "@emotion/styled";
import { last, map, get, first } from "lodash";
import moment from "moment";
import Icon from "atoms/Icon";
import EditAvailableCount from "organisms/EditAvailableCount";
import InfoIcon from "@mui/icons-material/Info";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { getLeaveCategoryList } from "redux/selectors";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

const StyledChartContainer = styled.div`
  border-radius: 3px;
  padding: 10px;
  margin-bottom: 15px;
  box-shadow: 0px 4px 27px 0px #00000017;
  .styled-circle {
    height: 30px;
    width: 30px;
    svg {
      margin: 4px;
    }
  }
  .css-1ps7utq {
    margin-bottom: 0 !important;
  }
  .css-1180qxj {
    margin-bottom: 0 !important;
  }
  .css-1ig93pz {
    margin-bottom: 0 !important;
  }
  .report-title {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    font-size: 0.8rem;
    padding-top: 3px;
    padding-left: 10px;
    text-transform: uppercase;
  }
  .report-title1 {
    font-weight: bold;
    font-size: 0.7rem;
    padding-top: 3px;
  }
  .legends {
    padding-top: 2rem;
    padding-left: 5px;
    border-radius: 5px;
    text-align: left;
    div {
      padding-bottom: 5px;
    }
  }
  .leave_tracker_header {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  .leave_header_text {
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
    color: #1f243b;
  }
`;

const StyledColorBox = styled.span`
  ${({ color }) => color && `background: ${color};`}
  border-radius: 4px;
  padding: 0px 8px;
  margin-right: 10px;
`;

function LeaveTrackerChart({
  iconName,
  title,
  data,
  staffLeave,
  leaveId,
  userId,
  getUserLeaveData,
  leaveCategoryList,
  leaveTypeName,
  canEdit = false,
}) {
  const { t } = useTranslation();

  const totalLeaveAvailable = get(first(data), "value", 0);
  const totalLeaveConsumed = get(last(data), "value", 0);
  const leaveAvailable = totalLeaveAvailable;
  const [open, setOpen] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const config = {
    width: 120,
    height: 120,
    appendPadding: 6,
    data,
    angleField: "value",
    colorField: "type",
    color: leaveId === "5" ? get(last(data), "color") : map(data, "color", []),
    radius: 1,
    innerRadius: 0.8,
    tooltip: false,
    legend: false,
    label: false,
    statistic: {
      title: false,
      content: {
        style: {
          fontSize: 13,
        },
        content:
          leaveId === 5
            ? `${totalLeaveConsumed}`
            : `${totalLeaveConsumed}/${leaveAvailable}`,
      },
    },
  };

  return (
    <StyledChartContainer>
      <Row>
        <Col span={24} className="report-title">
          <div className="w-100 d-flex justify-content-between align-items-center leave_tracker_header">
            <p className="">
              {title} - {moment(new Date()).format("yyyy")}
            </p>
            <div className="d-flex align-items-center">
              {leaveCategoryList?.find(
                (item) => String(item?.leave_category_id) === String(leaveId)
              )?.rollout === "Yes" && (
                <div>
                  <span>
                    <InfoIcon
                      sx={{
                        color: "#1976d2",
                        fontSize: "20px",
                        marginRight: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => setOpen(true)}
                    />
                  </span>
                  {`${data[0]?.carryforward}` !== null ? (
                    <span>
                      {t("CF")}: <b>{`${data[0]?.carryforward}`}</b> {t("Days")}
                    </span>
                  ) : null}
                </div>
              )}
              {canEdit && (
                <BorderColorIcon
                  sx={{
                    color: "#1976d2",
                    fontSize: "18px",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => setOpenEditModal(true)}
                />
              )}
            </div>
          </div>
        </Col>

        <Col span={12}>
          <Pie {...config} />
        </Col>

        <Col span={12} className="legends">
          {leaveId !== "5" && (
            <div>
              <StyledColorBox color={get(first(data), "color")} />
              <span>
                {get(first(data), "type", "")} :{" "}
                {data[0].value == null ? "null" : data[0].value}
              </span>
            </div>
          )}

          <div>
            <StyledColorBox color={get(last(data), "color")} />
            <span>
              {get(last(data), "type", "")} :{" "}
              {data[1].value == null ? "null" : data[1].value}
            </span>
          </div>

          {(staffLeave === "Annual Leave" ||
            staffLeave === "Department's Annual Leave Balance") && (
            <div className="report-title1">
              {t("Last Year Leave Balance")} :{" "}
              <span style={{ color: "blue", fontWeight: "normal" }}>
                {get(first(data), "lastYearLeaveBalance", "")}
              </span>
            </div>
          )}
        </Col>
      </Row>

      <Modal
        header={null}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={600}
        closeIcon={
          <span style={{ fontSize: "20px", color: "#8c8c8c" }}>×</span>
        }
      >
        <>
          <span style={{ fontSize: "20px", fontWeight: 500, color: "#262626" }}>
            {t("Carry – Forward Leave (CF)")}
          </span>
          <div style={{ padding: "12px 0" }}>
            <p
              style={{
                color: "#8c8c8c",
                fontSize: "15px",
                lineHeight: "1.7",
                marginBottom: "20px",
              }}
            >
              {t(
                "Carry-Forward leave represents last year's unused leave brought into this year. These days are available only for a limited time and will be removed once the validity period ends."
              )}
            </p>

            <p
              style={{
                color: "#262626",
                fontSize: "15px",
                fontWeight: 500,
                margin: 0,
              }}
            >
              {t("Validity till")}:{" "}
              {(() => {
                const expiryMonth = leaveCategoryList?.find(
                  (item) => String(item?.leave_category_id) === String(leaveId)
                )?.expiry_date;

                if (!expiryMonth) return "-";

                return moment(expiryMonth, "MMMM")
                  .endOf("month")
                  .format("DD MMMM YYYY");
              })()}
              <span style={{ color: "#8c8c8c", fontWeight: 400 }}></span>
            </p>
          </div>
        </>
      </Modal>
      {canEdit && (
        <EditAvailableCount
          isModalOpen={openEditModal}
          handleCancel={() => setOpenEditModal(false)}
          name={t("Edit Available Count")}
          leaveAvailable={leaveAvailable}
          leaveTypeName={leaveTypeName || title}
          leaveId={leaveId}
          userId={userId}
          getUserLeaveData={getUserLeaveData}
        />
      )}
    </StyledChartContainer>
  );
}

const mapStateToProps = (state) => ({
  leaveCategoryList: getLeaveCategoryList(state),
});

export default connect(mapStateToProps, null)(LeaveTrackerChart);
